import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';
import translations from '../../translation'; // Import translation file

const PendingTable = ({ language }) => {
  const translate = (key) => translations[language]?.[key] || key; // Translation function
  const [modalShow, setModalShow] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingData, setPendingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    fetchPendingData();
  }, []);

  const fetchPendingData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('user_id', user.id);
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval/pending`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success && data.data && data.data.length > 0) {
        setPendingData(data.data || []);
      } else {
        setPendingData([]); // Set to an empty array to ensure no data is displayed
        console.warn(data.message || 'No pending data found.');
      }
    } catch (error) {
      console.error('Error fetching pending data:', error);
      setPendingData([]); // Ensure no stale data is displayed on error
    } finally {
      setIsLoading(false);
    }
  };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setModalShow(true);
  };
  const handleApprove = (taskId) => {
    Swal.fire({
      title: translate('approveConfirmTitle'),
      text: translate('approveConfirmText'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: translate('confirmButton'),
    }).then((result) => {
      if (result.isConfirmed) {
        approveTask(taskId);
      }
    });
  };

  const handleReject = (taskId) => {
    Swal.fire({
      title: translate('rejectConfirmTitle'),
      text: translate('rejectConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: translate('confirmButton'),
    }).then((result) => {
      if (result.isConfirmed) {
        rejectTask(taskId);
      }
    });
  };

  const approveTask = async (taskId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('id', taskId);
      formData.append('user_id', user.id);
      formData.append('status', 'Approved');
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate('approveSuccessTitle'), translate('approveSuccessText'), 'success');
        fetchPendingData(); // Refresh the data
      } else {
        Swal.fire(translate('errorTitle'), data.message, 'error');
      }
    } catch (error) {
      console.error('Error approving task:', error);
      Swal.fire(translate('errorTitle'), translate('errorText'), 'error');
    }
  };

  const rejectTask = async (taskId) => {
    try {
      const { value: reason } = await Swal.fire({
        title: translate('rejectReasonTitle'),
        input: 'textarea',
        inputLabel: translate('reasonLabel'),
        inputPlaceholder: translate('reasonPlaceholder'),
        inputAttributes: {
          'aria-label': translate('reasonAriaLabel'),
        },
        showCancelButton: true,
        confirmButtonText: translate('submitButton'),
        cancelButtonText: translate('cancelButton'),
      });

      if (!reason) {
        Swal.fire(translate('cancelledTitle'), translate('cancelledText'), 'info');
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('id', taskId);
      formData.append('user_id', user.id);
      formData.append('status', 'Rejected');
      formData.append('reject_reason', reason);
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate('rejectSuccessTitle'), translate('rejectSuccessText'), 'success');
        fetchPendingData(); // Refresh the data
      } else {
        Swal.fire(translate('errorTitle'), data.message, 'error');
      }
    } catch (error) {
      console.error('Error rejecting task:', error);
      Swal.fire(translate('errorTitle'), translate('errorText'), 'error');
    }
  };

  // Pagination Logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = pendingData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(pendingData.length / rowsPerPage);

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{translate('pendingTableTitle')}</h3>
      </div>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="px-4 py-6 text-center text-gray-500">{translate('loadingText')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('titleLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('submittedByLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('dateLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('statusLabel')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('actionsLabel')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(row.sent_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {translate('pending')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleView(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button mr-2"
                      >
                        {translate('viewButton')}
                      </button>
                      <button
                        onClick={() => handleApprove(row.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-button mr-2"
                      >
                        {translate('approveButton')}
                      </button>
                      <button
                        onClick={() => handleReject(row.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-button"
                      >
                        {translate('rejectButton')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
              currentPage === 1 ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {translate('prevButton')}
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
              currentPage === totalPages ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {translate('nextButton')}
          </button>
      </div>

      <Modal
        size="xl"
        show={modalShow}
        onHide={() => setModalShow(false)}
        centered
        dialogClassName="modal-75w"
      >
        <Modal.Header closeButton>
          <Modal.Title>{translate('taskDetailsTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{translate('fieldLabel')}</th>
                <th>{translate('valueLabel')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{translate('taskTitleLabel')}</td>
                <td>{selectedTask?.title || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('taskContentLabel')}</td>
                <td>{selectedTask?.content || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('submittedByLabel')}</td>
                <td>{selectedTask?.username || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalStatusLabel')}</td>
                <td>{translate('pending') || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalDateLabel')}</td>
                <td>
                  {selectedTask?.approved_date
                    ? new Date(selectedTask?.approved_date).toLocaleDateString()
                    : translate('notApproved')}
                </td>
              </tr>
              <tr>
                <td>{translate('rejectionReasonLabel')}</td>
                <td>{selectedTask?.rejection_reason || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('methodLabel')}</td>
                <td>{selectedTask?.method || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('createdAtLabel')}</td>
                <td>
                  {selectedTask?.created_at
                    ? new Date(selectedTask?.created_at).toLocaleDateString()
                    : translate('notAvailable')}
                </td>
              </tr>
              <tr>
                <td>{translate('sentAtLabel')}</td>
                <td>
                  {selectedTask?.sent_at
                    ? new Date(selectedTask?.sent_at).toLocaleDateString()
                    : translate('notAvailable')}
                </td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PendingTable;
